import React from 'react';
import { useForm, Controller, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useMonitoringSettings } from 'containers/admin/clients/client/tabs-components/monitoring-settings-modal/use-monitoring-settings';

import Select from 'components/partials/select/select';
import MaskedInput from 'components/shared/others/masked-input/masked-input';
import Loader from 'components/partials/loader/loader';

import {
  STAGES_OPTIONS,
  SELECT_STYLES,
  FORM_DEFAULT_VALUES,
} from 'containers/admin/clients/client/tabs-components/monitoring-settings-modal/monitoring-settings.constants';
import type { MonitoringFormValues, YearOption } from 'containers/admin/clients/client/tabs-components/monitoring-settings-modal/monitoring-settings.constants';

import {
  generateYear1Options,
  getCurrentFiscalYear,
  validationSchema,
} from 'containers/admin/clients/client/tabs-components/monitoring-settings-modal/monitoring-settings.helpers';
import { DATE_FORMAT } from 'containers/user/goals/goals-monitoring/monitoring-chart/hooks/constants';

interface MonitoringSettingsFormProps {
  clientId: string | number | undefined;
  userId: string | number | undefined;
  formId: string;
}

const MonitoringSettingsForm = ({
  clientId,
  userId,
  formId,
}: MonitoringSettingsFormProps): React.JSX.Element => {
  const { handleSubmit, control, reset } = useForm<MonitoringFormValues>({
    defaultValues: FORM_DEFAULT_VALUES,
    resolver: yupResolver(
      validationSchema
    ) as unknown as Resolver<MonitoringFormValues>,
  });

  const year1Options = generateYear1Options();
  const fiscalYear = getCurrentFiscalYear();

  const { loading, submitting, onSubmit, cohortOptions } =
    useMonitoringSettings({
      clientId,
      userId,
      reset,
    });

  return (
    <form
      id={formId}
      className="flex flex-col gap-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Loader
        loading={loading || submitting}
        customClass="!bg-white opacity-50"
        fullScreen={false}
      />
      <Controller
        control={control}
        name="cohort"
        render={({ field: { onChange, value } }) => (
          <Select
            label="Cohort"
            options={cohortOptions}
            value={value ?? undefined}
            onChange={onChange}
            required
            labelInTop={true}
            extraBasicStyles={SELECT_STYLES}
          />
        )}
      />

      <div>
        <div className="text-gray-700 font-semibold leading-6 text-sm">
          Comparison Years
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Controller
            control={control}
            name="comparisonYear1"
            render={({ field: { onChange, value } }) => (
              <Select<YearOption>
                label="Year 1"
                options={year1Options}
                value={value ?? undefined}
                onChange={onChange}
                required
                labelInTop
                extraBasicStyles={SELECT_STYLES}
              />
            )}
          />

          <Controller
            control={control}
            name="comparisonYear2"
            render={({ field: { onChange, value } }) => (
              <Select<YearOption>
                label="Year 2"
                options={[{ label: String(fiscalYear), value: fiscalYear }]}
                value={
                  value ?? { label: String(fiscalYear), value: fiscalYear }
                }
                onChange={onChange}
                disabled
                labelInTop
                extraBasicStyles={SELECT_STYLES}
              />
            )}
          />
        </div>
        <div>
          <div className="text-gray-700 font-semibold leading-6 mt-10">
            Funnel stages start date
          </div>

          <div className="flex flex-col gap-4">
            {STAGES_OPTIONS.map((stage) => (
              <Controller
                key={stage.value}
                control={control}
                name={stage.value}
                render={({
                  field: { onChange, onBlur, value },
                  fieldState: { error },
                }) => (
                  <MaskedInput
                    label={stage.label}
                    placeholder={DATE_FORMAT}
                    labelInTop={true}
                    mask="ddmmyy"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    inputCustomClass="h-9"
                    showMask={false}
                    errorsNames={error ? { message: error.message } : {}}
                  />
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </form>
  );
};

export default MonitoringSettingsForm;
